import { APP_CONSTANTS } from '@/config/constants';
import {
  CHECKLIST_SYNC_RECOVERY_FEATURE,
  IS_CHECKLIST_SYNC_RECOVERY_CANARY,
} from '@/config/recoveryCanary';
import { databaseService } from '@/services/databaseService';
import { isRecoveryFeatureEnabledForIdentity } from '@/services/recovery/RecoveryRuntimeGate';
import { supabase } from '@/services/supabase';
import { getRuntimeClientBuild } from '@/services/runtime/clientBuild';
import {
  PENDING_OPERATION_ACCOUNTED_DISABLED,
  PENDING_OPERATION_ACCOUNTED_FEATURE,
  SUPERSEDED_BY_REMOTE_ENTITY,
  emptyWriteSetDigest,
  resolveAllAccountedTerminalConflicts,
  resolveAccountedTerminalConflict,
  terminalizeSupersededByRemoteEntity,
  type PendingOperationSnapshotInput,
} from './PendingOperationTerminalization';
import { createSupabasePendingOperationLedger } from './SupabasePendingOperationLedger';

const lookupRemoteFeature = async (input: {
  companyId: string;
  userId: string;
  featureKey: string;
  clientBuild: number;
}): Promise<boolean> => {
  const { data, error } = await supabase
    .from('guincho_company_features')
    .select('enabled,min_build,config')
    .eq('empresa_id', input.companyId)
    .eq('feature_key', input.featureKey)
    .maybeSingle();
  if (error) throw error;
  return isRecoveryFeatureEnabledForIdentity(data, input.userId, input.clientBuild);
};

const assertAuthorized = async (input: {
  companyId: string;
  userId: string;
  clientBuild: number;
}): Promise<void> => {
  if (!IS_CHECKLIST_SYNC_RECOVERY_CANARY) {
    throw new Error(PENDING_OPERATION_ACCOUNTED_DISABLED);
  }
  const recoveryEnabled = await lookupRemoteFeature({
    ...input,
    featureKey: CHECKLIST_SYNC_RECOVERY_FEATURE,
  });
  const accountedEnabled = await lookupRemoteFeature({
    ...input,
    featureKey: PENDING_OPERATION_ACCOUNTED_FEATURE,
  });
  if (!recoveryEnabled || !accountedEnabled) {
    throw new Error(PENDING_OPERATION_ACCOUNTED_DISABLED);
  }
};

const ledger = () => createSupabasePendingOperationLedger({
  rpc: async (name, args) => {
    const result = await supabase.rpc(name, args);
    return { data: result.data, error: result.error };
  },
});

const lookupSuccessor = async (companyId: string, successorEntityId: string) => {
  const checklist = await databaseService.readItem<Record<string, unknown>>(
    APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
    successorEntityId
  );
  const id = typeof checklist?.id === 'string' ? checklist.id.toLowerCase() : '';
  const empresaId = typeof checklist?.empresa_id === 'string'
    ? checklist.empresa_id.toLowerCase()
    : '';
  if (!id || !empresaId) return null;
  return { id, empresaId };
};

export const runControlledSupersededTerminalization = async (input: {
  companyId: string;
  userId: string;
  recoveryId: string;
  receiptId: string;
  operation: PendingOperationSnapshotInput;
  expectedPayloadSha512: string;
  successorEntityId: string;
  writeSetCount?: number;
  writeSetDigest?: string;
  clientBuild?: number;
}) => {
  const clientBuild = input.clientBuild ?? getRuntimeClientBuild();
  await assertAuthorized({
    companyId: input.companyId,
    userId: input.userId,
    clientBuild,
  });
  return terminalizeSupersededByRemoteEntity({
    database: databaseService as any,
    ledger: ledger(),
    lookupSuccessor,
    companyId: input.companyId,
    userId: input.userId,
    recoveryId: input.recoveryId,
    receiptId: input.receiptId,
    clientBuild,
    operation: input.operation,
    expectedPayloadSha512: input.expectedPayloadSha512,
    successorEntityId: input.successorEntityId,
    writeSetCount: input.writeSetCount ?? 0,
    writeSetDigest: input.writeSetDigest ?? emptyWriteSetDigest(),
  });
};

export const runControlledAccountedConflictResolution = async (input: {
  companyId: string;
  userId: string;
  conflictId: string;
  accountedArchiveId: string;
  expectedPayloadSha512: string;
  clientBuild?: number;
}) => {
  const clientBuild = input.clientBuild ?? getRuntimeClientBuild();
  await assertAuthorized({
    companyId: input.companyId,
    userId: input.userId,
    clientBuild,
  });
  return resolveAccountedTerminalConflict({
    database: databaseService as any,
    conflictId: input.conflictId,
    accountedArchiveId: input.accountedArchiveId,
    expectedPayloadSha512: input.expectedPayloadSha512,
    companyId: input.companyId,
    userId: input.userId,
  });
};

export const runControlledAccountedConflictResolutionBatch = async (input: {
  companyId: string;
  userId: string;
  clientBuild?: number;
}) => {
  const clientBuild = input.clientBuild ?? getRuntimeClientBuild();
  await assertAuthorized({
    companyId: input.companyId,
    userId: input.userId,
    clientBuild,
  });
  return resolveAllAccountedTerminalConflicts({
    database: databaseService as any,
    companyId: input.companyId,
    userId: input.userId,
  });
};

export const SUPERSEDED_ACCOUNTING_STATE = SUPERSEDED_BY_REMOTE_ENTITY;
